import { useEffect, useMemo, useRef, useState } from "react";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import styles from "./VariantSelector.scss";
import {
  getInStockVariants,
  getVariantType,
} from "../../../utils/variantUtils";
import { VARIANT_TYPE } from "../../constants";
import {
  areAllInStockVariantsTheSamePrice,
  areAllOOSVariantsTheSamePrice,
} from "../../utils/discountPriceData";
import { useProduct } from "../../context/product/ProductContext";
import { useVariant } from "../../context/variant/VariantContext";
import { useOptionalMySizeProfile } from "../../context/mySizeProfile/MySizeProfileContext";
import SizeAndFitContainer from "../sizeAndFitContainer/SizeAndFitContainer";
import PropTypes from "prop-types";
import eventBusEmitter from "../../utils/eventBusEmitter";
import { handleModalTriggerEvent } from "../../utils/handleModalTriggerEvent";
import { shouldDisplayVariantSelectBasedOnVariants } from "../../utils/product";
import setComponentPerformance from "../../utils/setComponentPerformance";
import { getFeature } from "../../utils/features";
import { useFeaturesReady } from "../../context/featuresReady/FeaturesReadyContext";
import { MY_SIZE_PROFILE_OPEN_MODAL_EVENT } from "../mySizeProfile/constants";
import { categoryMeasurementMap } from "../../utils/category/measurementMap";
import {
  getSizeGuide,
  getMeasurementForSize,
  extractCategoryIdFromDescription,
} from "../../utils/sizeGuideUtils";
import { extractSizeGuideId } from "../../utils/extractSizeGuideId";
import {
  fireMspRecommendationDisplayed,
  fireMspSizeSelected,
} from "../../utils/marketingTracking/marketingTrackingEvents";

const renderSizeProfilePrompt = (handleAddSizeProfile, getTranslation) => {
  const sizeProfileMessage = getTranslation("pdp_my_size_profile_message");
  const linkToken = "%{link}";
  const hasLinkToken = sizeProfileMessage?.includes(linkToken);
  const [messageBeforeLink = "", messageAfterLink = ""] =
    sizeProfileMessage?.split(linkToken) ?? [];

  return (
    <div className={styles.recommendedSizeContent}>
      <div className={`${styles.rulerIcon} product-ruler`} />
      <div className={styles.sizeProfilePromptText}>
        {hasLinkToken ? messageBeforeLink : sizeProfileMessage}
        <button
          type="button"
          className={styles.recommendedSizeHelp}
          onClick={handleAddSizeProfile}
        >
          {getTranslation("pdp_my_size_profile_add_link")}
        </button>
        {hasLinkToken ? messageAfterLink : null}
      </div>
    </div>
  );
};

const getPlaceholderText = ({
  variants,
  variantType,
  getTranslation,
  shouldUseSizeProfilePromptCopy,
}) => {
  const label =
    variantType === VARIANT_TYPE.size
      ? getTranslation(
          shouldUseSizeProfilePromptCopy
            ? "pdp_select_a_size_label"
            : "pdp_select_size_label",
        )
      : getTranslation("pdp_select_colour_from_label", {
          count: variants.length,
        });
  return (
    <option key={variantType} value="">
      {label}
    </option>
  );
};

const addVariantPrice = ({ variants, variant }) => {
  const displayInStockPrice = areAllInStockVariantsTheSamePrice({ variants })
    ? ""
    : ` - ${variant.price.current.text}`;

  const displayOOSPrice = areAllOOSVariantsTheSamePrice({ variants })
    ? ""
    : ` - ${variant.price.current.text}`;

  return variant.isInStock ? displayInStockPrice : displayOOSPrice;
};

const addOutOfStockLabel = ({ variant, getTranslation }) =>
  variant.isInStock ? "" : ` - ${getTranslation("pdp_status_out_of_stock")}`;

const sortVariants = ({ variants }) => {
  return variants.sort((currentVariant, nextVariant) => {
    const colourComparisonResult = currentVariant.colour.localeCompare(
      nextVariant.colour,
      undefined,
      {
        numeric: true,
      },
    );

    return colourComparisonResult === 0
      ? currentVariant.sizeOrder - nextVariant.sizeOrder
      : colourComparisonResult;
  });
};

const populateVariantSelector = ({
  variants,
  variantType,
  getTranslation,
  oosVariantsAreSelectable,
  measurementLookup,
  bodyMeasurement,
  isPrimaryDimensionEnabled,
  shouldUseSizeProfilePromptCopy,
}) => {
  const shouldCheckMeasurements =
    isPrimaryDimensionEnabled && variantType === VARIANT_TYPE.size;

  return [
    getPlaceholderText({
      variants,
      variantType,
      getTranslation,
      shouldUseSizeProfilePromptCopy,
    }),
    ...sortVariants({ variants }).map((variant, index) => {
      const measurement = measurementLookup[variant.brandSize];
      const fullLabel = variant[variantType] || "";
      const baseLabel =
        fullLabel.split(/[\s-]*(?:chest|waist|bust)/i)[0].trim() || fullLabel;
      const alreadyHasMeasurement = /\b(waist|chest|bust)[\s\-:]*\d+/i.test(
        fullLabel,
      );

      const shouldShowMeasurement =
        shouldCheckMeasurements && measurement && !alreadyHasMeasurement;
      const displayText = `${fullLabel}${
        shouldShowMeasurement ? ` - ${bodyMeasurement}: ${measurement}` : ""
      }${addVariantPrice({ variants, variant })}${addOutOfStockLabel({
        variant,
        getTranslation,
      })}`;

      const ariaLabel = shouldShowMeasurement
        ? `${baseLabel}, ${bodyMeasurement} ${measurement}${addVariantPrice({
            variants,
            variant,
          })}${addOutOfStockLabel({ variant, getTranslation })}`
        : displayText;

      return (
        <option
          key={variant.variantId}
          value={variant.variantId}
          disabled={!(oosVariantsAreSelectable || variant.isInStock)}
          data-testid={`${variantType}-${index}`}
          aria-label={ariaLabel}
        >
          {displayText}
        </option>
      );
    }),
  ];
};

const getLabelText = (variantType, getTranslation, shouldHideSizeLabel) => {
  if (variantType === VARIANT_TYPE.size) {
    return shouldHideSizeLabel ? "" : `${getTranslation("pdp_misc_size")}:`;
  }

  return `${getTranslation("pdp_misc_colour")}:`;
};

const getBrandSizeForRecommendation = ({
  recommendation,
  productVariants,
  index,
}) => {
  const recommendedSizeId = recommendation?.brandSizes?.[index]?.sizeId;

  if (!recommendedSizeId || !Array.isArray(productVariants)) {
    return null;
  }

  const matchingVariant = productVariants.find(
    (variant) => variant.sizeId === recommendedSizeId,
  );

  return matchingVariant?.brandSize ?? null;
};

const replaceSizeGuideMessagePlaceholders = ({
  message,
  recommendation,
  productVariants,
}) => {
  if (!message) {
    return null;
  }

  let result = message;

  // Handle {0}, {1} style placeholders by looking up sizeId in brandSizes array
  if (Array.isArray(productVariants) && recommendation?.brandSizes) {
    // {0} = first brandSize
    const firstSizeId = recommendation.brandSizes[0]?.sizeId;
    if (firstSizeId) {
      const firstVariant = productVariants.find(
        (variant) => variant.sizeId === firstSizeId,
      );
      if (firstVariant?.brandSize) {
        result = result.replace(/\{0\}/g, firstVariant.brandSize);
      }
    }

    // {1} = second brandSize
    const secondSizeId = recommendation.brandSizes[1]?.sizeId;
    if (secondSizeId) {
      const secondVariant = productVariants.find(
        (variant) => variant.sizeId === secondSizeId,
      );
      if (secondVariant?.brandSize) {
        result = result.replace(/\{1\}/g, secondVariant.brandSize);
      }
    }
  }

  const tighterFitSize = getBrandSizeForRecommendation({
    recommendation,
    productVariants,
    index: 0,
  });
  const looserFitSize = getBrandSizeForRecommendation({
    recommendation,
    productVariants,
    index: 1,
  });

  // Handle {{1:size}} and {{2:size}} style placeholders
  result = result
    .replace(/\{\{1:size\}\}/g, tighterFitSize ? tighterFitSize : "{{1:size}}")
    .replace(/\{\{2:size\}\}/g, looserFitSize ? looserFitSize : "{{2:size}}");

  return result;
};

const escapeHtml = (value = "") =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const sanitizeSizeGuideMessageHtml = (message) => {
  if (!message) {
    return null;
  }

  return escapeHtml(message)
    .replace(/&lt;(\/?\s*b\s*)&gt;/gi, "<$1>")
    .replace(/&lt;(\/?\s*strong\s*)&gt;/gi, "<$1>");
};

const shouldRenderVariantSelector = ({
  isInStock,
  product,
  oosVariantsAreSelectable,
}) => {
  return (
    shouldDisplayVariantSelectBasedOnVariants(
      product,
      oosVariantsAreSelectable,
    ) &&
    (oosVariantsAreSelectable || isInStock) &&
    !!product
  );
};

const getRecommendedSizeCount = ({ recommendation, productVariants }) => {
  const recommendedSizes = recommendation?.brandSizes;

  if (!Array.isArray(recommendedSizes) || !recommendedSizes.length) {
    return 0;
  }

  const productSizeIds = new Set(
    (productVariants || [])
      .map((variant) => variant?.sizeId)
      .filter((sizeId) => sizeId !== null && sizeId !== undefined),
  );

  if (!productSizeIds.size) {
    return recommendedSizes.length;
  }

  return recommendedSizes.filter((size) => productSizeIds.has(size.sizeId))
    .length;
};

const getVariants = ({ product, variantType, oosVariantsAreSelectable }) =>
  product
    ? variantType === VARIANT_TYPE.size || oosVariantsAreSelectable
      ? product.variants
      : getInStockVariants(product.variants)
    : [];

export default function VariantSelector({
  isNoVariantSelectionError,
  setShowNoVariantSelectionError,
  isInStock,
  variantSelectorId = "variantSelector",
}) {
  const { product } = useProduct();
  const { selectedVariantId, setSelectedVariantId } = useVariant();
  const {
    mySizeProfile,
    sizeGuideRecommendation,
    isEligible,
    loading,
    dismissed,
  } = useOptionalMySizeProfile();
  const { getTranslation, oosVariantsAreSelectable, storeCode } =
    useRuntimeConstants();
  const variantSelectorRef = useRef();
  const sizeHelpButtonRef = useRef();
  const recommendationDisplayTrackingRef = useRef(null);
  const { areFeaturesReady } = useFeaturesReady();
  const isPrimaryDimensionEnabled = areFeaturesReady
    ? (getFeature("pdp-primary-measurement-dimension")?.isEnabled() ?? false)
    : false;
  const variantType = getVariantType({ product });
  const isSizeVariant = variantType === VARIANT_TYPE.size;
  const variants = useMemo(
    () =>
      getVariants({
        product,
        variantType,
        oosVariantsAreSelectable,
      }),
    [product, variantType, oosVariantsAreSelectable],
  );
  const [sizeGuideData, setSizeGuideData] = useState(null);
  const bodyMeasurement = categoryMeasurementMap.find(
    (item) =>
      item.id === extractCategoryIdFromDescription(product?.description),
  )?.measurement;
  const sizeGuideId = extractSizeGuideId(product?.sizeGuide);

  useEffect(() => {
    if (
      isPrimaryDimensionEnabled &&
      sizeGuideId &&
      bodyMeasurement &&
      variantType === VARIANT_TYPE.size
    ) {
      getSizeGuide(sizeGuideId)
        .then(setSizeGuideData)
        .catch(() => {
          setSizeGuideData(null);
        });
    }
  }, [isPrimaryDimensionEnabled, sizeGuideId, bodyMeasurement, variantType]);

  const measurementLookup = useMemo(() => {
    if (!sizeGuideData || !bodyMeasurement) {
      return {};
    }
    return variants.reduce((acc, variant) => {
      acc[variant.brandSize] = getMeasurementForSize(
        sizeGuideData,
        variant.brandSize,
        bodyMeasurement,
        storeCode,
      );
      return acc;
    }, {});
  }, [sizeGuideData, bodyMeasurement, variants, storeCode]);

  const showVariantSelector = shouldRenderVariantSelector({
    oosVariantsAreSelectable,
    isInStock,
    product,
  });

  const sizeGuideMessage = sizeGuideRecommendation?.sizeGuideMessage || null;

  const sizeGuideMessageWithSizes = replaceSizeGuideMessagePlaceholders({
    message: sizeGuideMessage,
    recommendation: sizeGuideRecommendation,
    productVariants: product?.variants,
  });

  const safeSizeGuideMessageHtml = sanitizeSizeGuideMessageHtml(
    sizeGuideMessageWithSizes,
  );

  const showSizeGuideMessage =
    isSizeVariant && isInStock && !!safeSizeGuideMessageHtml;

  const showSizeProfilePrompt =
    isSizeVariant &&
    isInStock &&
    isEligible &&
    !mySizeProfile &&
    !showSizeGuideMessage &&
    !loading;

  const shouldOpenSizeProfileModal = showSizeProfilePrompt && !dismissed;

  const showSizeProfileMessaging =
    showSizeProfilePrompt || showSizeGuideMessage;

  const variantOptions = useMemo(
    () =>
      populateVariantSelector({
        variants,
        variantType,
        getTranslation,
        oosVariantsAreSelectable,
        measurementLookup,
        bodyMeasurement,
        isPrimaryDimensionEnabled,
        shouldUseSizeProfilePromptCopy: showSizeProfileMessaging,
      }),
    [
      variants,
      variantType,
      getTranslation,
      oosVariantsAreSelectable,
      measurementLookup,
      bodyMeasurement,
      isPrimaryDimensionEnabled,
      showSizeProfileMessaging,
    ],
  );

  useEffect(() => {
    if (isNoVariantSelectionError) {
      variantSelectorRef.current?.focus();
    }
  }, [isNoVariantSelectionError]);

  useEffect(() => {
    const shouldTrackRecommendationDisplay =
      showVariantSelector &&
      variantType === VARIANT_TYPE.size &&
      isInStock &&
      isEligible &&
      !loading &&
      !!mySizeProfile &&
      !!product;

    if (!shouldTrackRecommendationDisplay) {
      return;
    }

    const recommendedSizeCount = getRecommendedSizeCount({
      recommendation: sizeGuideRecommendation,
      productVariants: product.variants,
    });

    const trackingKey = `${product.id}-${recommendedSizeCount}`;
    if (recommendationDisplayTrackingRef.current === trackingKey) {
      return;
    }

    recommendationDisplayTrackingRef.current = trackingKey;
    fireMspRecommendationDisplayed({
      product,
      recommendedSizeCount,
    });
  }, [
    showVariantSelector,
    variantType,
    isInStock,
    isEligible,
    loading,
    mySizeProfile,
    product,
    sizeGuideRecommendation,
  ]);

  if (!showVariantSelector) {
    return null;
  }

  setComponentPerformance({ metric: "pdp:size_selector_interactive" });

  return (
    <div data-testid="variant-selector">
      <div className={styles.sizingHelp}>
        <label htmlFor={variantSelectorId} id={styles.label}>
          {getLabelText(variantType, getTranslation, showSizeProfileMessaging)}
        </label>
        <SizeAndFitContainer />
      </div>
      {showSizeGuideMessage && (
        <div className={styles.recommendedSize} data-testid="recommended-size">
          <div className={styles.recommendedSizeContent}>
            <div className={`${styles.rulerIcon} product-ruler`} />
            <div className={styles.recommendedSizeLabel}>
              <span
                dangerouslySetInnerHTML={{
                  __html: safeSizeGuideMessageHtml,
                }}
              />
              <button
                type="button"
                className={styles.recommendedSizeHelp}
                data-testid="recommended-size-help"
                ref={sizeHelpButtonRef}
                onClick={() => {
                  eventBusEmitter("sizeHelpOpen", {
                    focusOnClose: sizeHelpButtonRef.current,
                  });
                }}
              >
                {getTranslation("pdp_my_size_profile_edit_link")}
              </button>
            </div>
          </div>
        </div>
      )}
      {showSizeProfilePrompt && (
        <div
          className={styles.recommendedSize}
          data-testid="size-profile-prompt"
        >
          <div className={styles.recommendedSizeLabel}>
            {renderSizeProfilePrompt(() => {
              eventBusEmitter("sizeHelpOpen", {
                focusOnClose: variantSelectorRef.current,
              });
            }, getTranslation)}
          </div>
        </div>
      )}
      <div className={styles.variantSelectorContainer}>
        <select
          key={variantSelectorId}
          id={variantSelectorId}
          ref={variantSelectorRef}
          aria-label={
            isSizeVariant && showSizeProfileMessaging
              ? getTranslation("pdp_misc_size")
              : undefined
          }
          required={isNoVariantSelectionError}
          value={selectedVariantId ? `${selectedVariantId}` : ""}
          onMouseDown={(event) => {
            if (!shouldOpenSizeProfileModal) {
              return;
            }

            event.preventDefault();
            handleModalTriggerEvent({
              focusOnCloseElement: variantSelectorRef.current,
              eventKey: MY_SIZE_PROFILE_OPEN_MODAL_EVENT,
            });
          }}
          onChange={({ target }) => {
            const nextVariantId = target?.value ? parseInt(target.value) : null;
            setSelectedVariantId(nextVariantId);

            if (
              nextVariantId &&
              variantType === VARIANT_TYPE.size &&
              isInStock &&
              isEligible
            ) {
              const selectedSizeId = variants.find(
                (variant) => variant.variantId === nextVariantId,
              )?.sizeId;
              const recommendedSizeIds = new Set(
                (sizeGuideRecommendation?.brandSizes || []).map(
                  (brandSize) => brandSize.sizeId,
                ),
              );

              if (
                recommendedSizeIds.size > 0 &&
                selectedSizeId !== null &&
                selectedSizeId !== undefined
              ) {
                fireMspSizeSelected({
                  product,
                  isRecommended: recommendedSizeIds.has(selectedSizeId),
                });
              }
            }

            if (target?.value) {
              setShowNoVariantSelectionError(false);
            }
          }}
        >
          {variantOptions}
        </select>
      </div>
    </div>
  );
}

VariantSelector.propTypes = {
  isNoVariantSelectionError: PropTypes.bool.isRequired,
  setShowNoVariantSelectionError: PropTypes.func.isRequired,
  isInStock: PropTypes.bool,
  variantSelectorId: PropTypes.string,
};
